import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "../components/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import instance from "../api/axios";
import mixpanel from "../mixpanel";
import { getTherapistById, createFreelanceBooking } from "../api/route";
import EditFormModel from "../components/Models/editFormModel";
import ConfirmModel from "../components/Models/confirmModel";
import FailModel from "../components/Models/failModel";
import BackHeader from "../components/Home/BackHeader";
import StepIndicator from "../components/Home/StepIndicator";
import DiscountCard from "../components/SessionDetail/DiscountCard";
import AppointmentDetailsCard from "../components/SessionDetail/AppointmentDetailsCard";
import PatientInfoCard from "../components/SessionDetail/PatientInfoCard";
import NotesCard from "../components/SessionDetail/NotesCard";
import AddressCard from "../components/SessionDetail/AddressCard";
import SpecialInstructionCard from "../components/SessionDetail/SpecialInstructionCard";
import {
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";
import { CircularProgress } from "@mui/joy";
import DoctorAssignedCard from "../components/SessionDetail/DoctorAssignedCard";
import { usePartner } from "../context/PartnerContext";
import { DEFAULT_SESSION_FEE, getSessionFee } from "../utils/pricing";

const FreelanceBookingDetails = () => {
  const libraries = ["places"];
  const center = { lat: 19.076, lng: 72.8777 };

  const [address, setAddress] = useState("");
  const [floor, setFloor] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [therapist, setTherapist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [openEditModel, setOpenEditModel] = useState(false);
  const [Fees, setFees] = useState(DEFAULT_SESSION_FEE);
  const [notes, setNotes] = useState("");

  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [showFailModel, setShowFailModel] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [user, setUser] = useState(null);
  const [marker, setMarker] = useState(center);
  const [autocomplete, setAutocomplete] = useState(null);
  const [specialInstruction, setSpecialInstruction] = useState("");

  const mapRef = useRef();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerSlug } = useParams();
  const { config } = usePartner() || {};
  const { therapistId, timing } = state;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyBqKbHptbwiV5W2wsYy0J1G1x2AKk2aeRU",
    libraries,
  });

  const onLoad = useCallback((autocomplete) => {
    setAutocomplete(autocomplete);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const location = place.geometry.location;
        setMarker({ lat: location.lat(), lng: location.lng() });
        setAddress(place.formatted_address);
      }
    }
  }, [autocomplete]);

  useEffect(() => {
    console.log("FreelanceBookingDetails received state:", state);

    const fetchTherapist = async () => {
      setLoading(true);
      const userDetail = sessionStorage.getItem("user");
      const user = JSON.parse(userDetail);
      setUser(user);
      setFees(getSessionFee({ partnerSlug, config }));

      try {
        const response = await instance.get(`${getTherapistById}/${therapistId}`);
        setTherapist(response.data.therapist);
        console.log("Freelancer Therapist details:", response.data.therapist);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTherapist();
  }, [config, partnerSlug, state, therapistId]);

  useEffect(() => {
    setButtonDisabled(address.trim() === "" || floor.trim() === "");
  }, [address, floor]);

  const handleConfirmAppointment = async () => {
    setLoading(true);
    const userDetail = sessionStorage.getItem("user");
    const user = JSON.parse(userDetail);
    const obj = {
      doctor_id: therapist.doctor_id,
      user_name: user.name,
      user_phone: user.phone,
      user_pincode: user.pinCode,
      user_address: `${floor} ${address}`,
      user_city: user.address,
      therapist_name: therapist.doctor_name,
      therapist_email: therapist.email_id,
      profileImage: therapist.profileImage,
      time: timing.time,
      fees: Fees,
      user_condition: user.pain_condition || "",
      notes: notes || "",
      special_instruction: specialInstruction || "",
    };

    try {
      const response = await instance.post(createFreelanceBooking, obj);
      if (response?.data?.success) {
        const bookingData = response?.data?.bookingData;
        mixpanel.track("Button Clicked - Confirm Appointment (Freelancer)", {
          ...obj,
          "Booking Details": bookingData,
          Success: true,
        });

        navigate("/appointment-confirmation", { state: { bookingData } });
      } else {
        setErrorMessage(response?.data?.message);
        setShowFailModel(true);
        mixpanel.track("Failed - Confirm Appointment (Freelancer)", {
          "Doctor ID": obj.doctor_id,
          "Error Message": response?.data?.message,
        });
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setShowFailModel(true);
      mixpanel.track("Error - Confirm Appointment (Freelancer)", {
        "Doctor ID": obj.doctor_id,
        "Error Message": error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = new Date(timing.time).toLocaleDateString("en-us", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const hour = new Date(timing.time).toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

  const handleCloseConfirmModel = () => setShowConfirmModel(false);
  const handleCloseFailModel = () => setShowFailModel(false);
  const handleBackButtonClick = () => navigate(-1);

  if (loadError) return "Error loading maps";
  if (!isLoaded || loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="neutral" size="md" variant="plain" />
      </div>
    );
  }

  return (
    <div>
      <div className="min-h-screen md:px-0 flex justify-center px-0 py-0 md:py-6 md:px-4">
        <div
          className="w-full max-w-[700px] md:rounded-2xl md:shadow-lg md:bg-cover md:bg-no-repeat"
          style={{
            backgroundImage: `url(${require("../assets/images/BackgroundBig.png")})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="max-w-[700px] mx-auto">
            <BackHeader onBack={handleBackButtonClick} />
            <StepIndicator step={3} totalSteps={4} header="Confirm Appointment" />
            <DiscountCard />
            <DoctorAssignedCard
              doctorName={`Dr. ${therapist.doctor_name}`}
              doctorImage={therapist.profileImage}
            />
            <AppointmentDetailsCard date={formattedDate} time={hour} price={`\u20B9${Fees}`} />
            <PatientInfoCard
              patientName={user?.name}
              patientPhone={user?.phone}
              pincode={user?.pinCode}
              condition={user?.pain_condition || "N/A"}
              showEdit={true}
              onEditClick={() => setOpenEditModel(true)}
            />
            <AddressCard
              address={address}
              setAddress={setAddress}
              floor={floor}
              setFloor={setFloor}
              buttonDisabled={buttonDisabled}
              onLoad={onLoad}
              onPlaceChanged={onPlaceChanged}
            />
            <SpecialInstructionCard
              specialInstruction={specialInstruction}
              setSpecialInstruction={setSpecialInstruction}
            />
            <NotesCard notes={notes} setNotes={setNotes} />

            <div className="mt-0 p-4 flex flex-col items-center">
              <div className="w-80 ">
                <Button
                  btnName="Confirm Appointment"
                  className={`w-full offer-gradient text-center text-white font-semibold text-lg px-14 py-2 my-1.5 rounded-xl ${buttonDisabled ? "cursor-not-allowed opacity-50" : ""}`}
                  onClick={handleConfirmAppointment}
                  disabled={buttonDisabled}
                />
                <Button
                  btnName="Edit Details"
                  className="w-full text-center border border-[#FF9318] text-[#FF9318] font-semibold text-lg px-14 py-2 my-1.5 rounded-xl bg-transparent"
                  onClick={() => setOpenEditModel(true)}
                />
              </div>
            </div>

            {openEditModel && (
              <EditFormModel setopenEditModel={setOpenEditModel} />
            )}
            {showConfirmModel && bookingData && (
              <ConfirmModel
                onClose={handleCloseConfirmModel}
                bookingId={bookingData.booking_id}
                date={formattedDate}
                time={hour}
              />
            )}
            {showFailModel && (
              <FailModel onClose={handleCloseFailModel} errorMessage={errorMessage} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelanceBookingDetails;
